import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/DateInput'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { type InternalClaim, useSetClaimAdjuster } from '@/models/claim'

// Noon UTC stays the picked day for readers in every US timezone, unlike local midnight.
function asCalendarDay(date: Date): string {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12)).toISOString()
}

/**
 * The carrier tells us the adjuster by email or phone, often days after the fact,
 * so the assignment date is editable rather than the day staff typed it in.
 */
export function ClaimAdjusterDialog({
  claim,
  open,
  onOpenChange,
}: {
  claim: InternalClaim
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const setAdjuster = useSetClaimAdjuster()
  const assigned = claim.adjusterAssignedAt ? new Date(claim.adjusterAssignedAt) : undefined

  const [reference, setReference] = useState(claim.carrierReference)
  const [name, setName] = useState(claim.adjusterName)
  const [contact, setContact] = useState(claim.adjusterContact)
  const [date, setDate] = useState<Date | undefined>(assigned)

  // Keyed on opening, not on the values: a refetch landing mid-edit would wipe them.
  const loadedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      loadedFor.current = null
      return
    }
    if (loadedFor.current === claim.id) return
    loadedFor.current = claim.id
    const at = claim.adjusterAssignedAt ? new Date(claim.adjusterAssignedAt) : undefined
    setReference(claim.carrierReference)
    setName(claim.adjusterName)
    setContact(claim.adjusterContact)
    setDate(at)
  }, [open, claim])

  function save() {
    setAdjuster.mutate(
      {
        id: claim.id,
        name,
        contact,
        carrierReference: reference,
        assignedAt: date ? asCalendarDay(date) : undefined,
      },
      {
        onSuccess: () => {
          toast.success('Saved the carrier details.')
          onOpenChange(false)
        },
        onError: () => toast.error('The adjuster did not save.'),
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Carrier details on {claim.idShort}</DialogTitle>
          <DialogDescription>
            What the carrier has told us. The customer reads the reference on the filed step and the adjuster
            on the in-process step.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="carrier-reference">Carrier reference</Label>
            <Input
              id="carrier-reference"
              disabled={setAdjuster.isPending}
              value={reference}
              placeholder="FNOL-88213"
              onChange={(event) => setReference(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="adjuster-name">Adjuster name</Label>
            <Input
              id="adjuster-name"
              disabled={setAdjuster.isPending}
              value={name}
              placeholder="Jane Dawes"
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="adjuster-contact">Adjuster contact</Label>
            <Input
              id="adjuster-contact"
              disabled={setAdjuster.isPending}
              value={contact}
              placeholder="(312) 555-0147 or jane@carrier.com"
              onChange={(event) => setContact(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="adjuster-date">Assigned on</Label>
            <DateInput
              value={date}
              onChange={setDate}
              inputProps={{ id: 'adjuster-date', disabled: setAdjuster.isPending }}
            />
          </div>
          <p className="text-ink-500 text-xs">
            Leave the date empty to keep the recorded assignment date; with none recorded, the save date is
            used.
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button loading={setAdjuster.isPending ? 'Saving…' : undefined} onClick={save}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
